import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Default currency decimals used in most currencies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3, BIF: 0, BYR: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0, JOD: 3,
  JPY: 0, KMF: 0, KRW: 0, KWD: 3, LYD: 3, MRO: 5, OMR: 3, PYG: 0, RWF: 0, TND: 3,
  TWD: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
};

/**
 * A custom element that displays a UX product price with quantity-based total calculation.
 * This component listens for both quantity changes and variant updates.
 */
class UxProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initializePriceData();
    this.#addEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  /**
   * Initializes the base price data from the dataset.
   */
  #initializePriceData() {
    // Store original prices if not already stored
    if (!this.dataset.basePrice || !this.dataset.baseComparePrice) {
      const regularPriceEl = this.querySelector('[data-regular-price]');
      const salePriceEl = this.querySelector('[data-sale-price]');

      if (salePriceEl && salePriceEl.textContent) {
        // Extract price from formatted text (remove currency symbols, etc.)
        const priceText = salePriceEl.textContent.replace(/[^\d.,]/g, '');
        this.dataset.basePrice = this.#parsePriceText(priceText).toString();
      }

      if (regularPriceEl && regularPriceEl.textContent) {
        const comparePriceText = regularPriceEl.textContent.replace(/[^\d.,]/g, '');
        this.dataset.baseComparePrice = this.#parsePriceText(comparePriceText).toString();
      }
    }
  }

  /**
   * Adds event listeners for quantity changes and variant updates.
   */
  #addEventListeners() {
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange.bind(this));
      section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate.bind(this));
    }

    // If this is the main product price component, also listen for direct variant input changes
    if (this.closest('.product-info, .product-content, .product-details')) {
      const mainVariantInput = document.querySelector('form[id*="product-form"] input[name="id"]');
      if (mainVariantInput) {
        mainVariantInput.addEventListener('change', this.#handleMainVariantSelectorChange.bind(this));
      }
    }
  }

  /**
   * Removes event listeners.
   */
  #removeEventListeners() {
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      section.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange.bind(this));
      section.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate.bind(this));
    }

    // Remove main variant input listener if it was added
    if (this.closest('.product-info, .product-content, .product-details')) {
      const mainVariantInput = document.querySelector('form[id*="product-form"] input[name="id"]');
      if (mainVariantInput) {
        mainVariantInput.removeEventListener('change', this.#handleMainVariantSelectorChange.bind(this));
      }
    }
  }

    /**
   * Handles quantity selector update events.
   * @param {CustomEvent} event - The quantity selector update event.
   */
  #handleQuantityChange(event) {
    if (!event.detail || typeof event.detail.quantity !== 'number') return;

    const quantity = Math.max(1, event.detail.quantity);
    this.#updatePriceDisplay(quantity);
  }

  /**
   * Handles direct changes to the main variant selector.
   * @param {Event} event - The change event from the main variant selector.
   */
  #handleMainVariantSelectorChange(event) {
    // Note: Sticky variant selector sync is handled by the sticky component itself
    // This method is kept for potential future use but doesn't sync to avoid conflicts
    console.log('Main variant selector changed in ux-product-price component');
  }

  /**
   * Handles variant update events.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate(event) {
    // Check if this variant update is for this product
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    // Extract new prices from the updated HTML
    const newPriceHTML = event.detail.data.html;
    let newBasePrice, newBaseComparePrice, regularPriceText, salePriceText;

    // First try to find ux-product-price
    const newUxProductPrice = newPriceHTML.querySelector('ux-product-price');

    if (newUxProductPrice) {
      // Get base prices from data attributes
      newBasePrice = newUxProductPrice.dataset.basePrice;
      newBaseComparePrice = newUxProductPrice.dataset.baseComparePrice;

      // Get displayed prices
      const regularPriceEl = newUxProductPrice.querySelector('[data-regular-price]');
      const salePriceEl = newUxProductPrice.querySelector('[data-sale-price]');

      regularPriceText = regularPriceEl?.textContent || '';
      salePriceText = salePriceEl?.textContent || '';
    } else {
      // Fallback to regular product-price element
      const newProductPrice = newPriceHTML.querySelector('product-price');

      if (newProductPrice) {
        const priceContainer = newProductPrice.querySelector('[ref="priceContainer"]');
        const regularPriceEl = priceContainer?.querySelector('.price__regular');
        const salePriceEl = priceContainer?.querySelector('.price__sale .price-item--sale');

        if (salePriceEl?.textContent) {
          salePriceText = salePriceEl.textContent;
          const priceValue = salePriceEl.textContent.replace(/[^\d.,]/g, '');
          newBasePrice = this.#parsePriceText(priceValue).toString();
        }

        if (regularPriceEl?.textContent) {
          regularPriceText = regularPriceEl.textContent;
          const comparePriceValue = regularPriceEl.textContent.replace(/[^\d.,]/g, '');
          newBaseComparePrice = this.#parsePriceText(comparePriceValue).toString();
        }
      }
    }

    // Update base prices if we found them
    if (newBasePrice) {
      this.dataset.basePrice = newBasePrice;
    }

    if (newBaseComparePrice) {
      this.dataset.baseComparePrice = newBaseComparePrice;
    }

    // Update displayed prices to reflect the new variant (single quantity)
    if (regularPriceText) {
      const currentRegularPriceEl = this.querySelector('[data-regular-price]');
      if (currentRegularPriceEl) {
        currentRegularPriceEl.textContent = regularPriceText;
      }
    }

    if (salePriceText) {
      const currentSalePriceEl = this.querySelector('[data-sale-price]');
      if (currentSalePriceEl) {
        currentSalePriceEl.textContent = salePriceText;
      }
    }

    // Update sticky prices if this is the main product price component
    if (this.closest('.product-info, .product-content, .product-details')) {
      this.#updateStickyPricesFromVariant(regularPriceText, salePriceText);
      // Note: Sticky variant selector sync is handled by the sticky component itself
      // to avoid conflicts between multiple synchronization systems
    }

    // Get current quantity and recalculate total
    const currentQuantity = this.#getCurrentQuantity();
    if (currentQuantity > 1) {
      this.#updatePriceDisplay(currentQuantity);
    }
  }

  /**
   * Gets the current quantity from the quantity selector in the same section.
   * @returns {number} The current quantity.
   */
  #getCurrentQuantity() {
    const section = this.closest('.shopify-section');
    const quantityInput = section?.querySelector('quantity-selector-component input[ref="quantityInput"]');
    return quantityInput && quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.value) || 1 : 1;
  }

    /**
   * Updates the price display based on quantity.
   * @param {number} quantity - The selected quantity.
   */
  #updatePriceDisplay(quantity) {
    const basePrice = parseFloat(this.dataset.basePrice || '0') || 0;
    const baseComparePrice = parseFloat(this.dataset.baseComparePrice || '0') || 0;

    const newPrice = basePrice * quantity;
    const newComparePrice = baseComparePrice * quantity;

    // Update regular price (compare at price) - only within this component instance
    const regularPriceEl = this.querySelector('[data-regular-price]');
    if (regularPriceEl && baseComparePrice > 0) {
      regularPriceEl.textContent = this.formatMoney(newComparePrice);
    }

    // Update sale price (current price) - only within this component instance
    const salePriceEl = this.querySelector('[data-sale-price]');
    if (salePriceEl) {
      salePriceEl.textContent = this.formatMoney(newPrice);
    }

    // Also update any sticky add-to-cart prices if this is the main product price component
    if (this.closest('.product-info, .product-content, .product-details')) {
      this.#updateStickyPrices(newPrice, newComparePrice, baseComparePrice > 0);
    }
  }

  /**
   * Updates sticky add-to-cart prices to match the main product price.
   * @param {number} newPrice - The new price in cents.
   * @param {number} newComparePrice - The new compare price in cents.
   * @param {boolean} hasComparePrice - Whether there's a compare price to show.
   */
  #updateStickyPrices(newPrice, newComparePrice, hasComparePrice) {
    const stickyAddToCart = document.getElementById('sticky-add-to-cart');
    if (!stickyAddToCart) return;

    // Update sticky sale price
    const stickySalePriceEl = stickyAddToCart.querySelector('[data-sale-price]');
    if (stickySalePriceEl) {
      stickySalePriceEl.textContent = this.formatMoney(newPrice);
    }

    // Update sticky regular price (compare at price)
    const stickyRegularPriceEl = stickyAddToCart.querySelector('[data-regular-price]');
    if (stickyRegularPriceEl && hasComparePrice) {
      stickyRegularPriceEl.textContent = this.formatMoney(newComparePrice);
      if (stickyRegularPriceEl instanceof HTMLElement) {
        stickyRegularPriceEl.style.display = '';
      }
    } else if (stickyRegularPriceEl instanceof HTMLElement) {
      stickyRegularPriceEl.style.display = 'none';
    }
  }

  /**
   * Updates sticky add-to-cart prices from variant update with formatted text.
   * @param {string} regularPriceText - The formatted regular price text.
   * @param {string} salePriceText - The formatted sale price text.
   */
  #updateStickyPricesFromVariant(regularPriceText, salePriceText) {
    const stickyAddToCart = document.getElementById('sticky-add-to-cart');
    if (!stickyAddToCart) return;

    // Update sticky sale price
    if (salePriceText) {
      const stickySalePriceEl = stickyAddToCart.querySelector('[data-sale-price]');
      if (stickySalePriceEl) {
        stickySalePriceEl.textContent = salePriceText;
      }
    }

    // Update sticky regular price
    const stickyRegularPriceEl = stickyAddToCart.querySelector('[data-regular-price]');
    if (stickyRegularPriceEl) {
      if (regularPriceText) {
        stickyRegularPriceEl.textContent = regularPriceText;
        if (stickyRegularPriceEl instanceof HTMLElement) {
          stickyRegularPriceEl.style.display = '';
        }
      } else if (stickyRegularPriceEl instanceof HTMLElement) {
        stickyRegularPriceEl.style.display = 'none';
      }
    }
  }

  /**
   * Updates sticky add-to-cart variant selector to match the main form selection.
   * @param {string} variantId - The selected variant ID.
   */
  #updateStickyVariantSelector(variantId) {
    if (!variantId) return;

    const stickyAddToCart = document.getElementById('sticky-add-to-cart');
    if (!stickyAddToCart) return;

    // Update hidden select value
    const stickyVariantSelector = stickyAddToCart.querySelector('#sticky-variant-selector');
    if (stickyVariantSelector && stickyVariantSelector instanceof HTMLSelectElement) {
      stickyVariantSelector.value = variantId;
    }

    // Update dropdown display value
    const stickyDropdownValue = stickyAddToCart.querySelector('#sticky-dropdown-value');
    const selectedOption = stickyVariantSelector?.querySelector(`option[value="${variantId}"]`);

    if (stickyDropdownValue && selectedOption) {
      stickyDropdownValue.textContent = selectedOption.textContent || '';
    }

    // Update dropdown option states
    const stickyDropdownList = stickyAddToCart.querySelector('#sticky-dropdown-list');
    if (stickyDropdownList) {
      // Remove selected state from all options
      stickyDropdownList.querySelectorAll('.ux-custom-dropdown__option').forEach(option => {
        option.classList.remove('ux-custom-dropdown__option--selected');
        option.setAttribute('aria-selected', 'false');
      });

      // Add selected state to current option
      const selectedDropdownOption = stickyDropdownList.querySelector(`[data-value="${variantId}"]`);
      if (selectedDropdownOption) {
        selectedDropdownOption.classList.add('ux-custom-dropdown__option--selected');
        selectedDropdownOption.setAttribute('aria-selected', 'true');
      }
    }

    // Update add to cart button state based on variant availability
    const stickyAddToCartBtn = stickyAddToCart.querySelector('#sticky-add-to-cart-btn');
    const buttonText = stickyAddToCartBtn?.querySelector('[data-submit-button-text]');

    if (selectedOption && selectedOption instanceof HTMLOptionElement && stickyAddToCartBtn instanceof HTMLButtonElement && buttonText) {
      const isAvailable = !selectedOption.disabled;
      stickyAddToCartBtn.disabled = !isAvailable;

      // Update button text (preserving the icon)
      const iconHtml = buttonText.querySelector('svg')?.outerHTML || '';
      const textContent = isAvailable ? 'Add to cart' : 'Sold out';
      buttonText.innerHTML = iconHtml + textContent;
    }
  }

  /**
   * Parses price text to get numeric value in cents.
   * @param {string} priceText - The price text to parse.
   * @returns {number} The price in cents.
   */
  #parsePriceText(priceText) {
    if (!priceText) return 0;

    // Remove all non-digit and non-decimal characters, then convert to cents
    const cleanPrice = priceText.replace(/[^\d.]/g, '');
    const priceInDollars = parseFloat(cleanPrice) || 0;
    return Math.round(priceInDollars * 100); // Convert to cents
  }

  /**
   * Formats money, replicating the implementation of the `money` liquid filter
   * @param {number} moneyValue - The money value in cents
   * @returns {string} The formatted money value
   */
  formatMoney(moneyValue) {
    const moneyFormatTemplate = this.querySelector('template[ref="moneyFormat"]');
    if (!(moneyFormatTemplate instanceof HTMLTemplateElement)) {
      // Fallback formatting
      return (moneyValue / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'GBP',
      });
    }

    const template = moneyFormatTemplate.content.textContent || '{{amount}}';
    const currency = this.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }
}

if (!customElements.get('ux-product-price')) {
  customElements.define('ux-product-price', UxProductPrice);
}
